<script lang="ts">
  import PixelButton from '../components/PixelButton.svelte';
  import PixelCheckbox from '../components/PixelCheckbox.svelte';
  import PixelInput from '../components/PixelInput.svelte';
  import WaveText from '../components/WaveText.svelte';
  import { i18n } from '../i18n/index.svelte';
  import { auth } from '../stores/auth.svelte';
  import { router } from '../stores/router.svelte';
  import { session } from '../stores/session.svelte';
  import { toasts } from '../stores/toasts.svelte';
  import { authErrorField, authErrorMessage } from '../authMessages';

  /**
   * Sign in. Reference 07 · LOGIN.
   *
   * This is now the first screen a new device sees, and the shell resolves
   * every route to it until an identity exists. What has *not* changed is the
   * part that mattered: JUGAR COMO INVITADO is right there, it costs nothing,
   * and a player who takes it loses no feature. The screen asks who you are; it
   * does not ask you to register.
   *
   * A device that has already answered never comes back here — see
   * `session.init` — so this is a first-run screen and a sign-out destination,
   * not a toll gate on every launch.
   */

  const t = $derived(i18n.t);

  let identifier = $state('');
  let password = $state('');
  let rememberMe = $state(true);
  let busy = $state(false);
  let failure = $state<string | null>(null);
  let failedField = $state<string | null>(null);

  const canSubmit = $derived(identifier.trim().length > 0 && password.length > 0 && !busy);

  /**
   * JUGAR COMO INVITADO — the first screen's real primary action.
   *
   * This is where a guest is created now: the device mints its own name, takes
   * its seat, and only then tells the server. Previously the button merely
   * navigated, because a guest had already been created behind the player's
   * back during boot.
   *
   * It cannot fail in a way worth reporting. `playAsGuest` grants the identity
   * locally before any request, so no connection means an unsynced guest rather
   * than a rejected one — which is exactly what the offline case needs.
   */
  async function continueAsGuest(): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await session.playAsGuest();
      router.go('menu');
    } finally {
      busy = false;
    }
  }

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    busy = true;
    failure = null;
    failedField = null;

    try {
      const profile = await auth.login(identifier.trim(), password, rememberMe);
      session.adopt(profile);
      // Cleared on success only: a rejected attempt keeps the field filled so a
      // typo can be fixed without retyping everything.
      password = '';
      toasts.success(t.toasts.signedIn, { key: 'auth:state' });
      router.go('menu');
    } catch (error) {
      failure = authErrorMessage(error);
      failedField = authErrorField(error);
    } finally {
      busy = false;
    }
  }
</script>

<div class="view auth auth--login">
  <div class="auth__brand">
    <WaveText as="h1" class="auth__title" text="2K48" />
    <p class="auth__subtitle">{t.menu.subtitle}</p>
    <!-- The four player colours, in the order the rest of the app assigns
         them. Decorative, so it is hidden from assistive technology. -->
    <div class="auth__pips" aria-hidden="true">
      <span class="auth__pip auth__pip--p1"></span>
      <span class="auth__pip auth__pip--p2"></span>
      <span class="auth__pip auth__pip--accent"></span>
      <span class="auth__pip auth__pip--positive"></span>
    </div>
  </div>

  <form
    class="auth__form"
    onsubmit={(event) => {
      event.preventDefault();
      void submit();
    }}
  >
    <PixelInput
      label={t.auth.username}
      name="identifier"
      bind:value={identifier}
      autocomplete="username"
      maxlength={254}
      disabled={busy}
      error={failedField === 'identifier' ? (failure ?? undefined) : undefined}
    />

    <PixelInput
      label={t.auth.password}
      name="password"
      type="password"
      bind:value={password}
      autocomplete="current-password"
      maxlength={128}
      disabled={busy}
      error={failedField === 'password' ? (failure ?? undefined) : undefined}
    />

    <div class="auth__row">
      <PixelCheckbox
        label={t.auth.rememberMe}
        checked={rememberMe}
        disabled={busy}
        onchange={(next) => (rememberMe = next)}
      />
      <!--
        Disabled rather than removed: the mockup has it, and recovery needs a
        mail provider that has not been chosen. A dead link that silently does
        nothing is worse than a control that shows it is not ready yet.
      -->
      <button type="button" class="auth__forgot" disabled title={t.auth.forgotSoon}>
        {t.auth.forgot}
      </button>
    </div>

    <!--
      Height is reserved by CSS whether or not this is filled, so a failing
      attempt cannot slide the buttons up under a thumb that is already moving
      toward them.
    -->
    <p class="auth__failure" role="alert">{failedField === null && failure ? failure : ''}</p>

    <div class="auth__actions">
      <PixelButton type="submit" variant="p1" icon="play" disabled={!canSubmit}>
        {busy ? t.auth.signingIn : t.auth.signInTitle}
      </PixelButton>

      <PixelButton variant="ghost" disabled={busy} onclick={() => router.go('register')}>
        {t.auth.createAccount}
      </PixelButton>
    </div>
  </form>

  <button type="button" class="auth__guest" disabled={busy} onclick={() => void continueAsGuest()}>
    {t.auth.playAsGuest}
  </button>

  <p class="auth__coin">{t.auth.insertCoin}</p>
</div>
